import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ResponseError } from '../models/response-error';

/**
 * Error notification context containing error details and metadata.
 */
export interface ApiErrorContext {
  error: ResponseError;
  url?: string;
  statusCode?: number;
  errorType: string;
  timestamp: Date;
  shouldNotify: boolean;
}

/**
 * Service responsible for broadcasting API errors to subscribers.
 * Decouples error handling from UI notification logic.
 * 
 * @example
 * // In a component or service
 * constructor(private errorNotifier: ApiErrorNotifierService) {
 *   this.errorNotifier.errors$.subscribe(context => {
 *     if (context.shouldNotify) {
 *       this.dialogMessage.showResponseError(context.error);
 *     }
 *   });
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class ApiErrorNotifierService {
  private readonly _errorSubject = new Subject<ApiErrorContext>();

  /**
   * Observable stream of API errors.
   * Subscribe to this to handle errors in your application.
   */
  public readonly errors$: Observable<ApiErrorContext> = this._errorSubject.asObservable();

  /**
   * Notifies subscribers about an API error.
   * @param context - Error context with all relevant information
   */
  public notifyError(context: ApiErrorContext): void {
    this._errorSubject.next(context);
  }

  /**
   * Creates an error context from error details.
   */
  public createContext(
    error: ResponseError,
    errorType: string,
    shouldNotify: boolean,
    url?: string,
    statusCode?: number
  ): ApiErrorContext {
    return {
      error,
      url,
      statusCode,
      errorType,
      timestamp: new Date(),
      shouldNotify
    };
  }
}
